import React, { useState, useEffect } from 'react';
import { Wifi, RefreshCw, Cloud, CloudOff } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { db } from '@/lib/db';
import { cn } from '@/lib/utils';

interface SyncStatusBarProps {
  className?: string;
}

/**
 * Barre de statut de synchronisation
 * Affiche l'état de connexion et les opérations en attente
 */
export const SyncStatusBar: React.FC<SyncStatusBarProps> = ({ className }) => {
  const { isAuthenticated } = useAuth();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Écouter les événements online/offline
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Afficher temporairement quand on revient en ligne
      setIsVisible(true);
      setTimeout(() => {
        if (pendingCount === 0) setIsVisible(false);
      }, 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setIsVisible(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [pendingCount]);

  // Compter les opérations en attente
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkPending = async () => {
      try {
        const count = await db.getPendingCount();
        setPendingCount(count);
        setIsVisible(!isOnline || count > 0);
      } catch (e) {
        // Ignorer les erreurs
      }
    };

    checkPending();

    // Vérifier périodiquement
    const interval = setInterval(checkPending, 5000);
    return () => clearInterval(interval);
  }, [isAuthenticated, isOnline]);

  // Synchroniser manuellement
  const handleSync = async () => {
    if (!isOnline || isSyncing || pendingCount === 0) return;

    setIsSyncing(true);
    try {
      // Recharger la page pour forcer la synchronisation via OfflineSyncProvider
      window.location.reload();
    } catch (error) {
      console.error('Erreur sync:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  // Ne pas afficher si non authentifié ou si tout va bien
  if (!isAuthenticated || !isVisible) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300',
        className
      )}
    >
      <div
        className={cn(
          'text-white py-2 px-4 flex items-center justify-between',
          !isOnline
            ? 'bg-gradient-to-r from-orange-500 to-orange-600'
            : pendingCount > 0
            ? 'bg-gradient-to-r from-blue-500 to-blue-600'
            : 'bg-gradient-to-r from-green-500 to-green-600'
        )}
      >
        <div className="flex items-center gap-3">
          {!isOnline ? (
            <>
              <CloudOff size={18} />
              <span className="text-sm font-medium">Mode hors ligne</span>
            </>
          ) : pendingCount > 0 ? (
            <>
              <Cloud size={18} />
              <span className="text-sm font-medium">
                {pendingCount} modification{pendingCount > 1 ? 's' : ''} en attente
              </span>
            </>
          ) : (
            <>
              <Wifi size={18} />
              <span className="text-sm font-medium">Connecté</span>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isOnline && (
            <span className="text-xs opacity-80">
              Les modifications seront synchronisées au retour de la connexion
            </span>
          )}

          {isOnline && pendingCount > 0 && (
            <button
              onClick={handleSync}
              disabled={isSyncing}
              className="flex items-center gap-1 px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-sm transition-colors disabled:opacity-50"
            >
              <RefreshCw size={14} className={isSyncing ? 'animate-spin' : ''} />
              {isSyncing ? 'Sync...' : 'Synchroniser'}
            </button>
          )}

          {isOnline && pendingCount === 0 && (
            <span className="text-xs opacity-80">Tout est synchronisé</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default SyncStatusBar;
